// openEHR LANG model — BMM package org.openehr.lang.bmm_persistence
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { BMM_CLASS, BMM_CONTAINER_TYPE, BMM_ENUMERATION, BMM_ENUMERATION_INTEGER, BMM_ENUMERATION_STRING, BMM_GENERIC_TYPE, BMM_INDEXED_CONTAINER_TYPE, BMM_SIMPLE_TYPE, BMM_TYPE } from "../_shared.ts";
import { BMM_GENERIC_CLASS, BMM_PARAMETER_TYPE, BMM_SIMPLE_CLASS } from "../_shared.ts";
import { BMM_CONTAINER_PROPERTY, BMM_UNITARY_PROPERTY } from "../bmm/core/feature.ts";
import type { BMM_INDEXED_CONTAINER_PROPERTY, BMM_PROPERTY } from "../bmm/core/feature.ts";
import { BMM_PACKAGE } from "../bmm/core/model.ts";
import type { BMM_MODEL } from "../bmm/core/model.ts";
import { BMM_SCHEMA, BMM_SCHEMA_DESCRIPTOR } from "../bmm/model_access.ts";
import * as openehr_base from "../../base/mod.ts";

/**
 * Persistent form of \`BMM_MODEL_ELEMENT\`.
 */
export abstract class P_BMM_MODEL_ELEMENT {
  /**
   * Internal storage for documentation
   * @protected
   */
  protected _documentation?: openehr_base.String;

  /**
   * Optional documentation of this element.
   */
  get documentation(): string | undefined {
    return this._documentation?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for documentation.
   * Use this to access openehr_base.String methods.
   */
  get $documentation(): openehr_base.String | undefined {
    return this._documentation;
  }

  /**
   * Sets documentation from either a primitive value or openehr_base.String wrapper.
   */
  set documentation(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._documentation = undefined;
    } else if (typeof val === "string") {
      this._documentation = openehr_base.String.from(val);
    } else {
      this._documentation = val;
    }
  }
}

/**
 * Definition of persistent form of \`BMM_CLASS\` for serialisation to ODIN, JSON, XML etc.
 */
export class P_BMM_CLASS extends P_BMM_MODEL_ELEMENT {
  /**
   * Internal storage for name
   * @protected
   */
  protected _name?: openehr_base.String;

  /**
   * Name of the class. Persisted attribute.
   */
  get name(): string | undefined {
    return this._name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for name.
   * Use this to access openehr_base.String methods.
   */
  get $name(): openehr_base.String | undefined {
    return this._name;
  }

  /**
   * Sets name from either a primitive value or openehr_base.String wrapper.
   */
  set name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._name = undefined;
    } else if (typeof val === "string") {
      this._name = openehr_base.String.from(val);
    } else {
      this._name = val;
    }
  }

  /**
   * List of immediate inheritance parents. If there are generic ancestors, use \`_ancestor_defs_\` instead. Persisted attribute.
   */
  ancestors?: undefined;
  /**
   * List of attributes defined in this class. Persistent attribute.
   */
  properties?: undefined;
  /**
   * Internal storage for is_abstract
   * @protected
   */
  protected _is_abstract?: openehr_base.Boolean;

  /**
   * True if this is an abstract type. Persisted attribute.
   */
  get is_abstract(): boolean | undefined {
    return this._is_abstract?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_abstract.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_abstract(): openehr_base.Boolean | undefined {
    return this._is_abstract;
  }

  /**
   * Sets is_abstract from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_abstract(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_abstract = undefined;
    } else if (typeof val === "boolean") {
      this._is_abstract = openehr_base.Boolean.from(val);
    } else {
      this._is_abstract = val;
    }
  }

  /**
   * Internal storage for is_override
   * @protected
   */
  protected _is_override?: openehr_base.Boolean;

  /**
   * True if this class definition overrides one found in an included schema.
   */
  get is_override(): boolean | undefined {
    return this._is_override?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_override.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_override(): openehr_base.Boolean | undefined {
    return this._is_override;
  }

  /**
   * Sets is_override from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_override(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_override = undefined;
    } else if (typeof val === "boolean") {
      this._is_override = openehr_base.Boolean.from(val);
    } else {
      this._is_override = val;
    }
  }

  /**
   * List of generic parameter definitions. Persisted attribute.
   */
  generic_parameter_defs?: undefined;
  /**
   * Internal storage for source_schema_id
   * @protected
   */
  protected _source_schema_id?: openehr_base.String;

  /**
   * Reference to original source schema defining this class. Set during \`BMM_SCHEMA\` materialise. Useful for GUI tools to enable user to edit the schema file containing a given class (i.e. taking into account that a class may be in any of the schemas in a schema inclusion hierarchy).
   */
  get source_schema_id(): string | undefined {
    return this._source_schema_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for source_schema_id.
   * Use this to access openehr_base.String methods.
   */
  get $source_schema_id(): openehr_base.String | undefined {
    return this._source_schema_id;
  }

  /**
   * Sets source_schema_id from either a primitive value or openehr_base.String wrapper.
   */
  set source_schema_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._source_schema_id = undefined;
    } else if (typeof val === "string") {
      this._source_schema_id = openehr_base.String.from(val);
    } else {
      this._source_schema_id = val;
    }
  }

  /**
   * \`BMM_CLASS\` object built by \`_create_bmm_class_definition_\` and \`_populate_bmm_class_definition_\`.
   */
  bmm_class?: BMM_CLASS;
  /**
   * Internal storage for uid
   * @protected
   */
  protected _uid?: openehr_base.Integer;

  /**
   * Unique id generated for later comparison during merging, in order to detect if two classes are the same. Assigned in post-load processing.
   */
  get uid(): number | undefined {
    return this._uid?.value;
  }

  /**
   * Gets the openehr_base.Integer wrapper object for uid.
   * Use this to access openehr_base.Integer methods.
   */
  get $uid(): openehr_base.Integer | undefined {
    return this._uid;
  }

  /**
   * Sets uid from either a primitive value or openehr_base.Integer wrapper.
   */
  set uid(val: number | openehr_base.Integer | undefined) {
    if (val === undefined || val === null) {
      this._uid = undefined;
    } else if (typeof val === "number") {
      this._uid = openehr_base.Integer.from(val);
    } else {
      this._uid = val;
    }
  }

  /**
   * List of structured inheritance ancestors, used only in the case of generic inheritance. Persisted attribute.
   */
  ancestor_defs?: undefined;
  /**
   * True if this class is a generic class.
   * @returns Result value
   */
  is_generic(): openehr_base.Boolean {
    const defs = this.generic_parameter_defs as
      | unknown[]
      | Record<string, unknown>
      | undefined;
    if (!defs) return openehr_base.Boolean.from(false);
    if (Array.isArray(defs)) {
      return openehr_base.Boolean.from(defs.length > 0);
    }
    return openehr_base.Boolean.from(Object.keys(defs).length > 0);
  }

  /**
   * Create \`_bmm_class_definition_\`.
   * @returns Result value
   */
  create_bmm_class(): void {
    // Create a new BMM_CLASS from this persistent form
    if (this.is_generic().value === true) {
      this.bmm_class = new BMM_GENERIC_CLASS();
    } else {
      this.bmm_class = new BMM_SIMPLE_CLASS();
    }
    // Set basic properties
    if (this.name) {
      this.bmm_class.name = this.name;
    }
    if (this.is_abstract !== undefined) {
      this.bmm_class.is_abstract = this.is_abstract;
    }
  }

  /**
   * Add remaining model elements from Current to \`_bmm_class_definition_\`.
   * @param a_bmm_schema - Parameter
   * @returns Result value
   */
  populate_bmm_class(a_bmm_schema: BMM_MODEL): void {
    // Populate additional properties in the BMM class definition
    if (!this.bmm_class) {
      throw new Error("BMM class not created - call create_bmm_class first");
    }
    // Set source_schema_id if available
    this.bmm_class.source_schema_id = a_bmm_schema.model_id()?.value || "";
  }
}

/**
 * Persistent form of \`BMM_ENUMERATION\` attributes.
 */
export class P_BMM_ENUMERATION extends P_BMM_CLASS {
  item_names?: undefined;
  item_values?: undefined;
  /**
   * \`BMM_CLASS\` object build by \`_create_bmm_class_definition_\` and \`_populate_bmm_class_definition_\`.
   */
  override bmm_class?: BMM_ENUMERATION = undefined;
}

/**
 * Persisted form of a model component that contains packages.
 */
export class P_BMM_PACKAGE_CONTAINER {
  /**
   * Package structure as a hierarchy of packages each potentially containing names of classes in that package in the original model.
   */
  packages?: undefined;
}

/**
 * Persisted form of \`BMM_SCHEMA\`.
 */
export class P_BMM_SCHEMA extends BMM_SCHEMA {
  /**
   * Package structure as a hierarchy of packages (persisted).
   */
  packages?: undefined;
  /**
   * Primitive type definitions. Persisted attribute.
   */
  primitive_types?: undefined;
  /**
   * Class definitions. Persisted attribute.
   */
  class_definitions?: undefined;
  /**
   * Implementation of \`_validate_created()_\`
   * @returns Result value
   */
  validate_created(): void {
    // Validate that the schema was created correctly
    // Check that essential attributes are set
    if (!this.primitive_types && !this.class_definitions) {
      throw new Error("Schema has no type definitions");
    }
    // Additional validation can be added here
  }

  /**
   * Implementation of \`_load_finalise()_\`
   * @returns Result value
   */
  load_finalise(): void {
    // Finalize loading by converting packages to canonical form
    // and setting up include processing list
    // This is a stub - actual implementation would process package hierarchy
  }

  /**
   * Implementation of \`_merge()_\`
   * @param other - Parameter
   * @returns Result value
   */
  merge(other: P_BMM_SCHEMA): void {
    // Merge class and package definitions from other schema
    // Only merge if current schema doesn't have the definition
    if (other.class_definitions) {
      if (!this.class_definitions) {
        this.class_definitions = other.class_definitions;
      }
      // In real implementation, would merge individual definitions
    }
    if (other.primitive_types) {
      if (!this.primitive_types) {
        this.primitive_types = other.primitive_types;
      }
    }
  }

  /**
   * Implementation of \`_validate()_\`
   * @returns Result value
   */
  validate(): void {
    // Main validation prior to generation of BMM model
    // Validate class references, inheritance, etc.
    this.validate_created();
  }

  /**
   * Implementation of \`_create_bmm_model()_\`
   * @returns Result value
   */
  create_bmm_model(): void {
    // Create BMM_MODEL from this persisted schema
    // This would iterate through class definitions and create BMM classes
    // Stub implementation
  }

  /**
   * Package structure in which all top-level qualified package names like \`xx.yy.zz\` have been expanded out to a hierarchy of \`BMM_PACKAGE\` objects.
   * @returns Result value
   */
  canonical_packages(): P_BMM_PACKAGE {
    // Return the canonicalized package structure
    // Create a root package if packages exist
    const rootPkg = new P_BMM_PACKAGE();
    rootPkg.name = "root";
    return rootPkg;
  }
}

/**
 * Persistent form of \`BMM_PROPERTY\`.
 */
export abstract class P_BMM_PROPERTY extends P_BMM_MODEL_ELEMENT {
  /**
   * Internal storage for name
   * @protected
   */
  protected _name?: openehr_base.String;

  /**
   * Name of this property within its class. Persisted attribute.
   */
  get name(): string | undefined {
    return this._name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for name.
   * Use this to access openehr_base.String methods.
   */
  get $name(): openehr_base.String | undefined {
    return this._name;
  }

  /**
   * Sets name from either a primitive value or openehr_base.String wrapper.
   */
  set name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._name = undefined;
    } else if (typeof val === "string") {
      this._name = openehr_base.String.from(val);
    } else {
      this._name = val;
    }
  }

  /**
   * Internal storage for is_mandatory
   * @protected
   */
  protected _is_mandatory?: openehr_base.Boolean;

  /**
   * True if this property is mandatory in its class. Persisted attribute.
   */
  get is_mandatory(): boolean | undefined {
    return this._is_mandatory?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_mandatory.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_mandatory(): openehr_base.Boolean | undefined {
    return this._is_mandatory;
  }

  /**
   * Sets is_mandatory from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_mandatory(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_mandatory = undefined;
    } else if (typeof val === "boolean") {
      this._is_mandatory = openehr_base.Boolean.from(val);
    } else {
      this._is_mandatory = val;
    }
  }

  /**
   * Internal storage for is_computed
   * @protected
   */
  protected _is_computed?: openehr_base.Boolean;

  /**
   * True if this property is computed rather than stored in objects of this class. Persisted Attribute.
   */
  get is_computed(): boolean | undefined {
    return this._is_computed?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_computed.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_computed(): openehr_base.Boolean | undefined {
    return this._is_computed;
  }

  /**
   * Sets is_computed from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_computed(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_computed = undefined;
    } else if (typeof val === "boolean") {
      this._is_computed = openehr_base.Boolean.from(val);
    } else {
      this._is_computed = val;
    }
  }

  /**
   * Internal storage for is_im_infrastructure
   * @protected
   */
  protected _is_im_infrastructure?: openehr_base.Boolean;

  /**
   * True if this property is info model 'infrastructure' rather than 'data'. Persisted attribute.
   */
  get is_im_infrastructure(): boolean | undefined {
    return this._is_im_infrastructure?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_im_infrastructure.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_im_infrastructure(): openehr_base.Boolean | undefined {
    return this._is_im_infrastructure;
  }

  /**
   * Sets is_im_infrastructure from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_im_infrastructure(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_im_infrastructure = undefined;
    } else if (typeof val === "boolean") {
      this._is_im_infrastructure = openehr_base.Boolean.from(val);
    } else {
      this._is_im_infrastructure = val;
    }
  }

  /**
   * Internal storage for is_im_runtime
   * @protected
   */
  protected _is_im_runtime?: openehr_base.Boolean;

  /**
   * True if this property is info model 'runtime' settable property. Persisted attribute.
   */
  get is_im_runtime(): boolean | undefined {
    return this._is_im_runtime?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for is_im_runtime.
   * Use this to access openehr_base.Boolean methods.
   */
  get $is_im_runtime(): openehr_base.Boolean | undefined {
    return this._is_im_runtime;
  }

  /**
   * Sets is_im_runtime from either a primitive value or openehr_base.Boolean wrapper.
   */
  set is_im_runtime(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._is_im_runtime = undefined;
    } else if (typeof val === "boolean") {
      this._is_im_runtime = openehr_base.Boolean.from(val);
    } else {
      this._is_im_runtime = val;
    }
  }

  /**
   * Type definition of this property, if not a simple String type reference. Persisted attribute.
   */
  type_def?: P_BMM_TYPE;
  /**
   * BMM_PROPERTY created by create_bmm_property_definition.
   */
  bmm_property?: BMM_PROPERTY;
  /**
   * Create \`_bmm_property_definition_\` from \`P_BMM_XX\` parts.
   * @param a_bmm_schema - Parameter
   * @param a_class_def - Parameter
   * @returns Result value
   */
  create_bmm_property(a_bmm_schema: BMM_MODEL, a_class_def: BMM_CLASS): void {
    // Create BMM_PROPERTY from this persistent form
    this.bmm_property = new BMM_UNITARY_PROPERTY();
    // Set basic properties
    if (this.name) {
      this.bmm_property.name = this.name;
    }
    if (this.is_mandatory !== undefined) {
      this.bmm_property.is_nullable = !this.is_mandatory;
    }
    // Associate with the class definition
    this.bmm_property.scope = a_class_def;
  }
}

/**
 * Persistent form of a generic parameter (\`BMM_PARAMETER_TYPE\`).
 */
export class P_BMM_GENERIC_PARAMETER extends P_BMM_MODEL_ELEMENT {
  /**
   * Internal storage for name
   * @protected
   */
  protected _name?: openehr_base.String;

  /**
   * Name of the parameter, e.g. 'T' etc. Persisted attribute. Name is limited to 1 character, upper case.
   */
  get name(): string | undefined {
    return this._name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for name.
   * Use this to access openehr_base.String methods.
   */
  get $name(): openehr_base.String | undefined {
    return this._name;
  }

  /**
   * Sets name from either a primitive value or openehr_base.String wrapper.
   */
  set name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._name = undefined;
    } else if (typeof val === "string") {
      this._name = openehr_base.String.from(val);
    } else {
      this._name = val;
    }
  }

  /**
   * Internal storage for conforms_to_type
   * @protected
   */
  protected _conforms_to_type?: openehr_base.String;

  /**
   * Optional conformance constraint - the name of a type to which a concrete substitution of this generic parameter must conform. Persisted attribute.
   */
  get conforms_to_type(): string | undefined {
    return this._conforms_to_type?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for conforms_to_type.
   * Use this to access openehr_base.String methods.
   */
  get $conforms_to_type(): openehr_base.String | undefined {
    return this._conforms_to_type;
  }

  /**
   * Sets conforms_to_type from either a primitive value or openehr_base.String wrapper.
   */
  set conforms_to_type(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._conforms_to_type = undefined;
    } else if (typeof val === "string") {
      this._conforms_to_type = openehr_base.String.from(val);
    } else {
      this._conforms_to_type = val;
    }
  }

  /**
   * \`BMM_PARAMETER_TYPE\` created by \`create_bmm_generic_parameter\`.
   */
  bmm_generic_parameter?: BMM_PARAMETER_TYPE;
  /**
   * Create \`_bmm_generic_parameter_\`.
   * @param a_bmm_schema - Parameter
   * @returns Result value
   */
  create_bmm_generic_parameter(a_bmm_schema: BMM_MODEL): void {
    // Create BMM_PARAMETER_TYPE from this persistent form
    // For now, we create a parameter type representation
    // The actual BMM_PARAMETER_TYPE would need proper definition
    const paramType = new BMM_PARAMETER_TYPE();
    paramType.name = this.name || "";
    paramType.conforms_to_type = this.conforms_to_type;
    this.bmm_generic_parameter = paramType;
  }
}

/**
 * Persistent form of \`BMM_TYPE\`.
 */
export abstract class P_BMM_TYPE {
  /**
   * Result of \`_create_bmm_type()_\` call.
   */
  bmm_type?: BMM_TYPE;
  /**
   * Create appropriate \`BMM_XXX\` object; effected in descendants.
   * @param a_schema - Parameter
   * @param a_class_def - Parameter
   * @returns Result value
   */
  create_bmm_type(
    _a_schema: BMM_MODEL,
    _a_class_def: BMM_CLASS,
  ): void {
    throw new Error("Method create_bmm_type not yet implemented.");
  }

  /**
   * Formal name of the type for display.
   * @returns Result value
   */
  as_type_string(): openehr_base.String {
    throw new Error("Method as_type_string not yet implemented.");
  }
}

/**
 * Persistent form of \`BMM_CONTAINER_TYPE\`.
 */
export class P_BMM_CONTAINER_TYPE extends P_BMM_TYPE {
  /**
   * Internal storage for container_type
   * @protected
   */
  protected _container_type?: openehr_base.String;

  /**
   * The type of the container. This converts to the \`_root_type_\` in \`BMM_GENERIC_TYPE\`. Persisted attribute.
   */
  get container_type(): string | undefined {
    return this._container_type?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for container_type.
   * Use this to access openehr_base.String methods.
   */
  get $container_type(): openehr_base.String | undefined {
    return this._container_type;
  }

  /**
   * Sets container_type from either a primitive value or openehr_base.String wrapper.
   */
  set container_type(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._container_type = undefined;
    } else if (typeof val === "string") {
      this._container_type = openehr_base.String.from(val);
    } else {
      this._container_type = val;
    }
  }

  /**
   * Type definition of \`_type_\`, if not a simple String type reference. Persisted attribute.
   */
  type_def?: P_BMM_BASE_TYPE;
  /**
   * Internal storage for type
   * @protected
   */
  protected _type?: openehr_base.String;

  /**
   * The target type; this converts to the first parameter in \`_generic_parameters_\` in \`BMM_GENERIC_TYPE\`. Persisted attribute.
   */
  get type(): string | undefined {
    return this._type?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for type.
   * Use this to access openehr_base.String methods.
   */
  get $type(): openehr_base.String | undefined {
    return this._type;
  }

  /**
   * Sets type from either a primitive value or openehr_base.String wrapper.
   */
  set type(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._type = undefined;
    } else if (typeof val === "string") {
      this._type = openehr_base.String.from(val);
    } else {
      this._type = val;
    }
  }

  /**
   * Result of \`_create_bmm_type()_\` call.
   */
  override bmm_type?: BMM_CONTAINER_TYPE = undefined;
  /**
   * The target type; this converts to the first parameter in \`_generic_parameters_\` in \`BMM_GENERIC_TYPE\`. Persisted attribute.
   * @returns Result value
   */
  type_ref(): P_BMM_BASE_TYPE {
    // Return a simple type reference based on the type string
    const simpleType = new P_BMM_SIMPLE_TYPE();
    simpleType.type = this.type;
    return simpleType;
  }
}

/**
 * Persistent form of \`BMM_PROPER_TYPE\`.
 */
export abstract class P_BMM_BASE_TYPE extends P_BMM_TYPE {
  /**
   * Internal storage for value_constraint
   * @protected
   */
  protected _value_constraint?: openehr_base.String;

  get value_constraint(): string | undefined {
    return this._value_constraint?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for value_constraint.
   * Use this to access openehr_base.String methods.
   */
  get $value_constraint(): openehr_base.String | undefined {
    return this._value_constraint;
  }

  /**
   * Sets value_constraint from either a primitive value or openehr_base.String wrapper.
   */
  set value_constraint(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._value_constraint = undefined;
    } else if (typeof val === "string") {
      this._value_constraint = openehr_base.String.from(val);
    } else {
      this._value_constraint = val;
    }
  }
}

/**
 * Persistent form of \`BMM_SIMPLE_TYPE\`.
 */
export class P_BMM_SIMPLE_TYPE extends P_BMM_BASE_TYPE {
  /**
   * Internal storage for type
   * @protected
   */
  protected _type?: openehr_base.String;

  /**
   * Name of type - must be a simple class name.
   */
  get type(): string | undefined {
    return this._type?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for type.
   * Use this to access openehr_base.String methods.
   */
  get $type(): openehr_base.String | undefined {
    return this._type;
  }

  /**
   * Sets type from either a primitive value or openehr_base.String wrapper.
   */
  set type(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._type = undefined;
    } else if (typeof val === "string") {
      this._type = openehr_base.String.from(val);
    } else {
      this._type = val;
    }
  }

  /**
   * Result of \`_create_bmm_type()_\` call.
   */
  override bmm_type?: BMM_SIMPLE_TYPE = undefined;
}

/**
 * Persistent form of \`BMM_PARAMETER_TYPE\`.
 */
export class P_BMM_OPEN_TYPE extends P_BMM_BASE_TYPE {
  /**
   * Internal storage for type
   * @protected
   */
  protected _type?: openehr_base.String;

  /**
   * Simple type parameter as a single letter like 'T', 'G' etc.
   */
  get type(): string | undefined {
    return this._type?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for type.
   * Use this to access openehr_base.String methods.
   */
  get $type(): openehr_base.String | undefined {
    return this._type;
  }

  /**
   * Sets type from either a primitive value or openehr_base.String wrapper.
   */
  set type(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._type = undefined;
    } else if (typeof val === "string") {
      this._type = openehr_base.String.from(val);
    } else {
      this._type = val;
    }
  }

  /**
   * Result of \`_create_bmm_type()_\` call.
   */
  override bmm_type?: BMM_TYPE = undefined;
}

/**
 * Persistent form of \`BMM_GENERIC_TYPE\`.
 */
export class P_BMM_GENERIC_TYPE extends P_BMM_BASE_TYPE {
  /**
   * Internal storage for root_type
   * @protected
   */
  protected _root_type?: openehr_base.String;

  /**
   * Root type of this generic type, e.g. \`Interval\` in \`Interval<Integer>\`.
   */
  get root_type(): string | undefined {
    return this._root_type?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for root_type.
   * Use this to access openehr_base.String methods.
   */
  get $root_type(): openehr_base.String | undefined {
    return this._root_type;
  }

  /**
   * Sets root_type from either a primitive value or openehr_base.String wrapper.
   */
  set root_type(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._root_type = undefined;
    } else if (typeof val === "string") {
      this._root_type = openehr_base.String.from(val);
    } else {
      this._root_type = val;
    }
  }

  /**
   * Generic parameters of the root_type in this type specifier if non-simple types. The order must match the order of the owning class's formal generic parameter declarations. Persistent attribute.
   */
  generic_parameter_defs?: undefined;
  /**
   * Generic parameters of the \`_root_type_\` in this type specifier, if simple types. The order must match the order of the owning class's formal generic parameter declarations. Persistent attribute.
   */
  generic_parameters?: undefined;
  /**
   * Result of \`_create_bmm_type()_\` call.
   */
  override bmm_type?: BMM_GENERIC_TYPE = undefined;
  /**
   * Generic parameters of the root_type in this type specifier. The order must match the order of the owning class's formal generic parameter declarations
   * @returns Result value
   */
  generic_parameter_refs(): P_BMM_TYPE {
    // Return the first generic parameter definition or a simple type
    if (this.generic_parameter_defs) {
      const defs = this.generic_parameter_defs as unknown as P_BMM_TYPE[];
      if (defs.length > 0) {
        return defs[0];
      }
    }
    // Return a simple type based on first generic_parameters string
    const simpleType = new P_BMM_SIMPLE_TYPE();
    if (this.generic_parameters) {
      const params = this.generic_parameters as unknown as string[];
      if (params.length > 0) {
        simpleType.type = params[0];
      }
    }
    return simpleType;
  }
}

/**
 * Persisted form of a package as a tree structure whose nodes can contain more packages and/or classes.
 */
export class P_BMM_PACKAGE extends P_BMM_PACKAGE_CONTAINER {
  /**
   * Internal storage for name
   * @protected
   */
  protected _name?: openehr_base.String;

  /**
   * Name of the package from schema; this name may be qualified if it is a top-level package within the schema, or unqualified. Persistent attribute.
   */
  get name(): string | undefined {
    return this._name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for name.
   * Use this to access openehr_base.String methods.
   */
  get $name(): openehr_base.String | undefined {
    return this._name;
  }

  /**
   * Sets name from either a primitive value or openehr_base.String wrapper.
   */
  set name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._name = undefined;
    } else if (typeof val === "string") {
      this._name = openehr_base.String.from(val);
    } else {
      this._name = val;
    }
  }

  /**
   * List of classes in this package. Persistent attribute.
   */
  classes?: undefined;
  /**
   * \`BMM_PACKAGE\` created by \`_create_bmm_package_definition_\`.
   */
  bmm_package_definition?: BMM_PACKAGE;
  /**
   * Merge packages and classes from other (from an included \`P_BMM_SCHEMA\`) into this package.
   * @param other - Parameter
   * @returns Result value
   */
  merge(other: P_BMM_PACKAGE): void {
    // Merge classes from other package
    if (other.classes) {
      if (!this.classes) {
        this.classes = other.classes;
      }
      // In a complete implementation, would merge individual classes
    }
    // Merge packages recursively
    if (other.packages) {
      if (!this.packages) {
        this.packages = other.packages;
      }
    }
  }

  /**
   * Generate a \`BMM_PACKAGE_DEFINITION\` object and write it to \`_bmm_package_definition_\`.
   * @returns Result value
   */
  create_bmm_package_definition(): void {
    // Create BMM_PACKAGE from this persistent form
    this.bmm_package_definition = new BMM_PACKAGE();
    if (this.name) {
      this.bmm_package_definition.name = this.name;
    }
  }
}

/**
 * Persistent form of \`BMM_CONTAINER_PROPERTY\`.
 */
export class P_BMM_CONTAINER_PROPERTY extends P_BMM_PROPERTY {
  /**
   * Cardinality of this property in its class. Persistent attribute.
   */
  cardinality?: undefined;
  /**
   * Type definition of this property, if not a simple String type reference. Persistent attribute.
   */
  override type_def?: P_BMM_CONTAINER_TYPE = undefined;
  /**
   * \`BMM_PROPERTY\` created by \`_create_bmm_property_\`.
   */
  override bmm_property?: BMM_CONTAINER_PROPERTY = undefined;
  /**
   * Create \`_bmm_property_definition_\`.
   * @param a_bmm_schema - Parameter
   * @param a_class_def - Parameter
   * @returns Result value
   */
  create_bmm_property(a_bmm_schema: BMM_MODEL, a_class_def: BMM_CLASS): void {
    // Create BMM_CONTAINER_PROPERTY from this persistent form
    this.bmm_property = new BMM_CONTAINER_PROPERTY();
    if (this.name) {
      this.bmm_property.name = this.name;
    }
    if (this.is_mandatory !== undefined) {
      this.bmm_property.is_nullable = !this.is_mandatory;
    }
    // Associate with the class definition
    this.bmm_property.scope = a_class_def;
  }
}

/**
 * Persistent form of \`BMM_SINGLE_PROPERTY\`.
 */
export class P_BMM_SINGLE_PROPERTY extends P_BMM_PROPERTY {
  /**
   * Internal storage for type
   * @protected
   */
  protected _type?: openehr_base.String;

  /**
   * If the type is a simple type, then this attribute will hold the type name. If the type is a container or generic, then type_ref will hold the type definition. The resulting type is generated in type_def.
   */
  get type(): string | undefined {
    return this._type?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for type.
   * Use this to access openehr_base.String methods.
   */
  get $type(): openehr_base.String | undefined {
    return this._type;
  }

  /**
   * Sets type from either a primitive value or openehr_base.String wrapper.
   */
  set type(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._type = undefined;
    } else if (typeof val === "string") {
      this._type = openehr_base.String.from(val);
    } else {
      this._type = val;
    }
  }

  /**
   * Type definition of this property computed from \`_type_\` for later use in \`_bmm_property_\`.
   */
  type_ref?: P_BMM_SIMPLE_TYPE;
  /**
   * \`BMM_PROPERTY\` created by \`_create_bmm_property_definition_\`.
   */
  override bmm_property?: BMM_UNITARY_PROPERTY = undefined;
  /**
   * Generate \`_type_ref_\` from \`_type_\` and save.
   * @returns Result value
   */
  resolve_type_def(): P_BMM_SIMPLE_TYPE {
    if (!this.type_ref) {
      this.type_ref = new P_BMM_SIMPLE_TYPE();
      this.type_ref.type = this.type;
    }
    return this.type_ref;
  }
}

/**
 * Persistent form of a \`BMM_SINGLE_PROPERTY_OPEN\`.
 */
export class P_BMM_SINGLE_PROPERTY_OPEN extends P_BMM_PROPERTY {
  /**
   * Type definition of this property computed from \`_type_\` for later use in \`_bmm_property_\`.
   */
  type_ref?: P_BMM_OPEN_TYPE;
  /**
   * Internal storage for type
   * @protected
   */
  protected _type?: openehr_base.String;

  /**
   * Type definition of this property, if a simple String type reference. Really we should use \`_type_def_\` to be regular in the schema, but that makes the schema more wordy and less clear. So we use this persisted String value, and compute the \`_type_def_\` on the fly. Persisted attribute.
   */
  get type(): string | undefined {
    return this._type?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for type.
   * Use this to access openehr_base.String methods.
   */
  get $type(): openehr_base.String | undefined {
    return this._type;
  }

  /**
   * Sets type from either a primitive value or openehr_base.String wrapper.
   */
  set type(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._type = undefined;
    } else if (typeof val === "string") {
      this._type = openehr_base.String.from(val);
    } else {
      this._type = val;
    }
  }

  /**
   * \`BMM_PROPERTY\` created by \`_create_bmm_property_definition_\`.
   */
  override bmm_property?: BMM_UNITARY_PROPERTY = undefined;
  /**
   * Generate \`_type_ref_\` from \`_type_\` and save.
   * @returns Result value
   */
  resolve_type_def(): P_BMM_OPEN_TYPE {
    if (!this.type_ref) {
      this.type_ref = new P_BMM_OPEN_TYPE();
      this.type_ref.type = this.type;
    }
    return this.type_ref;
  }
}

/**
 * Persistent form of \`BMM_GENERIC_PROPERTY\`.
 */
export class P_BMM_GENERIC_PROPERTY extends P_BMM_PROPERTY {
  /**
   * Type definition of this property, if not a simple String type reference. Persistent attribute.
   */
  override type_def?: P_BMM_GENERIC_TYPE = undefined;
  /**
   * \`BMM_PROPERTY\` created by \`_create_bmm_property_definition_\`.
   */
  override bmm_property?: BMM_UNITARY_PROPERTY = undefined;
}

/**
 * Persistent form of an instance of \`BMM_ENUMERATION_INTEGER\`.
 */
export class P_BMM_ENUMERATION_INTEGER extends P_BMM_ENUMERATION {
  /**
   * \`BMM_CLASS\` object build by \`_create_bmm_class_definition_\` and \`_populate_bmm_class_definition_\`.
   */
  override bmm_class?: BMM_ENUMERATION_INTEGER = undefined;
}

/**
 * Persistent form of \`BMM_ENUMERATION_STRING\`.
 */
export class P_BMM_ENUMERATION_STRING extends P_BMM_ENUMERATION {
  /**
   * \`BMM_CLASS\` object build by \`_create_bmm_class_definition_\` and \`_populate_bmm_class_definition_\`.
   */
  override bmm_class?: BMM_ENUMERATION_STRING = undefined;
}

/**
 * Concrete descendant of \`BMM_SCHEMA_DESCRIPTOR\` that provides a way to read an ODIN or other similarly encoded P_BMM schema file.
 */
export class P_BMM_SCHEMA_DESCRIPTOR extends BMM_SCHEMA_DESCRIPTOR {
  /**
   * Persistent form of model.
   */
  override bmm_schema?: P_BMM_SCHEMA = undefined;
}

export class P_BMM_INDEXED_CONTAINER_PROPERTY extends P_BMM_CONTAINER_PROPERTY {
  /**
   * Type definition of this property, if not a simple String type reference. Persistent attribute.
   */
  override type_def?: P_BMM_INDEXED_CONTAINER_TYPE = undefined;
  /**
   * \`BMM_PROPERTY\` created by \`_create_bmm_property_\`.
   */
  override bmm_property?: BMM_INDEXED_CONTAINER_PROPERTY = undefined;
}

export class P_BMM_INDEXED_CONTAINER_TYPE extends P_BMM_CONTAINER_TYPE {
  /**
   * Internal storage for index_type
   * @protected
   */
  protected _index_type?: openehr_base.String;

  get index_type(): string | undefined {
    return this._index_type?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for index_type.
   * Use this to access openehr_base.String methods.
   */
  get $index_type(): openehr_base.String | undefined {
    return this._index_type;
  }

  /**
   * Sets index_type from either a primitive value or openehr_base.String wrapper.
   */
  set index_type(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._index_type = undefined;
    } else if (typeof val === "string") {
      this._index_type = openehr_base.String.from(val);
    } else {
      this._index_type = val;
    }
  }

  /**
   * Result of \`_create_bmm_type()_\` call.
   */
  override bmm_type?: BMM_INDEXED_CONTAINER_TYPE = undefined;
}

/**
 * Persistent form of a BMM function (routine) definition within a class (SPECLANG-16).
 */
export class P_BMM_FUNCTION extends P_BMM_MODEL_ELEMENT {
  name?: string | openehr_base.String;
  aliases?: Map<string, string>;
  is_abstract?: boolean | openehr_base.Boolean;
  parameters?: Map<string, P_BMM_FUNCTION_PARAMETER>;
  pre_conditions?: Map<string, string>;
  post_conditions?: Map<string, string>;
  result?: P_BMM_TYPE;
  is_nullable?: boolean | openehr_base.Boolean;
}

/**
 * Persistent form of a named function parameter.
 */
export abstract class P_BMM_FUNCTION_PARAMETER extends P_BMM_MODEL_ELEMENT {
  name?: string | openehr_base.String;
  is_nullable?: boolean | openehr_base.Boolean;
}

/** Parameter whose type is a simple named type. */
export class P_BMM_SINGLE_FUNCTION_PARAMETER extends P_BMM_FUNCTION_PARAMETER {
  type?: string | openehr_base.String;
}

/** Parameter whose type is an open generic parameter (`T`, `U`, …). */
export class P_BMM_SINGLE_FUNCTION_PARAMETER_OPEN
  extends P_BMM_FUNCTION_PARAMETER {
  type?: string | openehr_base.String;
}

/** Parameter whose type is a generic type such as `FUNCTION<TUPLE1<T>, Boolean>`. */
export class P_BMM_GENERIC_FUNCTION_PARAMETER extends P_BMM_FUNCTION_PARAMETER {
  type_def?: P_BMM_GENERIC_TYPE;
}

/** Parameter whose type is a container type. */
export class P_BMM_CONTAINER_FUNCTION_PARAMETER
  extends P_BMM_FUNCTION_PARAMETER {
  type_def?: P_BMM_CONTAINER_TYPE;
  cardinality?: { lower: number; upper?: number; upper_unbounded?: boolean };
}

/** Persistent form of a BMM constant. */
export class P_BMM_CONSTANT extends P_BMM_MODEL_ELEMENT {
  name?: string | openehr_base.String;
  type?: string | openehr_base.String;
  value?: string | openehr_base.String;
}

/** Persistent form of a BMM interface (function group). */
export class P_BMM_INTERFACE extends P_BMM_MODEL_ELEMENT {
  name?: string | openehr_base.String;
  functions?: Map<string, P_BMM_FUNCTION>;
}

